import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import Footer from './Footer';

const Profile = ({ navigateHome, navigateToPage }) => {
  const { user, isAuthenticated, logout, loading, getIdToken, refreshUserProfile } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [designerApplication, setDesignerApplication] = useState(null);
  const [loadingDesignerStatus, setLoadingDesignerStatus] = useState(false);

  // Fetch user orders when component mounts
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchUserOrders();
      fetchDesignerApplicationStatus();
    }
  }, [isAuthenticated, user]);

  const fetchDesignerApplicationStatus = async () => {
    setLoadingDesignerStatus(true);
    try {
      const idToken = await getIdToken();
      if (!idToken) return;

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      let apiUrl = backendUrl;
      if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
        apiUrl = '';
      }

      // Check user's specific designer application status
      const appResponse = await fetch(`${apiUrl}/api/marketplace/my-designer-application`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        credentials: 'include',
      });

      if (appResponse.ok) {
        const appData = await appResponse.json();
        setDesignerApplication(appData.has_application ? appData.application : null);
      } else {
        setDesignerApplication(null);
      }
    } catch (error) {
      console.error('Error fetching designer status:', error);
      setDesignerApplication(null);
    } finally {
      setLoadingDesignerStatus(false);
    }
  };

  const fetchUserOrders = async () => {
    setLoadingOrders(true);
    try {
      // Get Firebase ID token for authenticated request
      const idToken = await getIdToken();
      if (!idToken) {
        console.error('Unable to get authentication token');
        setLoadingOrders(false);
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      let apiUrl = backendUrl;
      if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
        apiUrl = '';
      }

      const response = await fetch(`${apiUrl}/api/orders/my-orders`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders);
      } else {
        console.error('Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  // Helper function to determine order type
  const getOrderType = (order) => {
    // Product orders have product_name and no quote_id
    if (order.product_name && !order.quote_id) {
      return 'product';
    }
    // Printing orders have quote_id or filename that doesn't start with "Cart Purchase"
    return 'printing';
  };

  // Handle navigation to specific sections
  const navigateToQuote = () => {
    // Navigate to home page with hash to instant quote section
    window.location.href = '/#instant-quote';
  };

  const navigateToServices = () => {
    // Navigate to services page
    navigateToPage('services');
  };
  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  // Component for product order display
  const ProductOrderCard = ({ order }) => (
    <div 
      className="border border-slate-200 rounded-lg p-4 hover:border-orange-300 hover:bg-orange-50 transition-colors cursor-pointer"
      onClick={() => handleOrderClick(order)}
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <h4 className="font-medium text-slate-900">{order.product_name}</h4>
          <p className="text-sm text-slate-600">Order #{order.id.slice(0, 8)}... • Product Purchase</p>
        </div>
        {getStatusBadge(order.status)}
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <p className="text-slate-500">Manufacturer</p>
          <p className="font-medium">{order.product_manufacturer || 'N/A'}</p>
        </div>
        <div>
          <p className="text-slate-500">Category</p>
          <p className="font-medium">{order.product_category || 'N/A'}</p>
        </div>
        <div>
          <p className="text-slate-500">Quantity</p>
          <p className="font-medium">{order.quantity || 1}</p>
        </div>
        <div>
          <p className="text-slate-500">Price</p>
          <p className="font-medium text-green-600">${order.final_price || order.estimated_price}</p>
        </div>
      </div>
      
      <div className="mt-3 flex justify-between items-center">
        <p className="text-xs text-slate-500">
          Ordered: {order.created_at ? (() => {
            try {
              const date = new Date(order.created_at);
              return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
            } catch (e) {
              return 'N/A';
            }
          })() : 'N/A'}
        </p>
        {order.tracking_number && (
          <p className="text-xs text-blue-600">
            Tracking: {order.tracking_number}
          </p>
        )}
      </div>
    </div>
  );

  // Component for printing order display
  const PrintingOrderCard = ({ order }) => (
    <div 
      className="border border-slate-200 rounded-lg p-4 hover:border-orange-300 hover:bg-orange-50 transition-colors cursor-pointer"
      onClick={() => handleOrderClick(order)}
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <h4 className="font-medium text-slate-900">{order.filename}</h4>
          <p className="text-sm text-slate-600">Order #{order.id.slice(0, 8)}... • 3D Printing Service</p>
        </div>
        {getStatusBadge(order.status)}
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <p className="text-slate-500">Material</p>
          <p className="font-medium">{order.material}</p>
        </div>
        <div>
          <p className="text-slate-500">Quality</p>
          <p className="font-medium">{order.quality}</p>
        </div>
        <div>
          <p className="text-slate-500">Price</p>
          <p className="font-medium text-green-600">${order.estimated_price}</p>
        </div>
        <div>
          <p className="text-slate-500">Delivery</p>
          <p className="font-medium">{order.delivery_weeks} weeks</p>
        </div>
      </div>
      
      {/* Additional printing details */}
      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-4 text-xs text-slate-500">
        <div>Color: {order.color}</div>
        <div>Strength: {order.strength}</div>
        <div>Smoothing: {order.smoothing}</div>
      </div>
      
      <div className="mt-3 flex justify-between items-center">
        <p className="text-xs text-slate-500">
          Created: {order.created_at ? (() => {
            try {
              const date = new Date(order.created_at);
              return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
            } catch (e) {
              return 'N/A';
            }
          })() : 'N/A'}
        </p>
        {order.tracking_number && (
          <p className="text-xs text-blue-600">
            Tracking: {order.tracking_number}
          </p>
        )}
      </div>
    </div>
  );

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'Pending' },
      confirmed: { color: 'bg-blue-100 text-blue-800', text: 'Confirmed' },
      in_production: { color: 'bg-purple-100 text-purple-800', text: 'In Production' },
      quality_check: { color: 'bg-indigo-100 text-indigo-800', text: 'Quality Check' },
      shipping: { color: 'bg-brand-orange-100 text-brand-orange-800', text: 'Shipping' },
      completed: { color: 'bg-brand-green-100 text-brand-green-800', text: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-800', text: 'Cancelled' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Access Denied</h2>
          <p className="text-slate-600 mb-6">You need to be logged in to access your profile.</p>
          <button 
            onClick={() => window.location.href = '/'}
            className="w-full bg-orange-600 text-white py-3 px-4 rounded-lg hover:bg-orange-700 transition-colors"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-center relative">
            <Button 
              onClick={() => window.location.href = '/'} 
              variant="outline"
              className="absolute left-0"
            >
              ← Back to Home
            </Button>
            <button onClick={() => window.location.href = '/'} className="flex items-center">
              <img 
                src="/logo-nav.png" 
                alt="Create 3D Works Logo" 
                className="h-10 sm:h-12 w-auto object-contain"
                style={{ minWidth: '120px' }}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Profile Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* User Info Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <div className="text-center">
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-20 h-20 rounded-full mx-auto mb-4 border-4 border-orange-100"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full mx-auto mb-4 bg-orange-100 flex items-center justify-center">
                    <span className="text-2xl font-bold text-orange-600">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{user.name}</h3>
                <p className="text-slate-600 mb-4 break-all text-sm">{user.email}</p>
                
                {/* Debug: Show user info */}
                {user.email && user.email.toLowerCase().includes('miker') && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-xs">
                    <p><strong>Debug Info:</strong></p>
                    <p>Email: {user.email}</p>
                    <p>Role: {user.role || 'undefined'}</p>
                    <p>is_admin: {user.is_admin ? 'true' : 'false'}</p>
                  </div>
                )}
                
                {/* ONE-TIME: Promote to Super Admin button - ALWAYS show for this email until admin */}
                {user.email && user.email.toLowerCase().includes('pitlanesimracing') && 
                 user?.role !== 'SUPER_ADMIN' && 
                 user?.role !== 'ADMIN' && 
                 !user?.is_admin ? (
                  <button
                    onClick={async () => {
                      if (!confirm('Click OK to promote yourself to Super Admin. This only needs to be done once.')) {
                        return;
                      }
                      try {
                        const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
                        const response = await fetch(`${apiUrl}/api/auth/promote-to-superadmin`, {
                          method: 'POST',
                          credentials: 'include'
                        });
                        const data = await response.json();
                        if (response.ok) {
                          alert(data.message);
                          window.location.reload();
                        } else {
                          alert('Error: ' + (data.detail || data.message || 'Unknown error'));
                        }
                      } catch (error) {
                        alert('Error promoting to admin: ' + error.message);
                      }
                    }}
                    className="w-full mb-4 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-bold py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg animate-pulse"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    🚀 Click Once to Activate Admin Access
                  </button>
                ) : null}
                
                {/* Admin Panel Access - Only visible to admins */}
                {(user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN' || user?.is_admin) && (
                  <button
                    onClick={() => navigateToPage('admin')}
                    className="w-full mb-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Admin Panel
                  </button>
                )}
                
                {/* Automatic Designer Status Display */}
                {loadingDesignerStatus ? (
                  <div className="mb-4">
                    <div className="animate-pulse flex items-center space-x-2">
                      <div className="w-4 h-4 bg-gray-200 rounded"></div>
                      <span className="text-sm text-gray-500">Loading designer status...</span>
                    </div>
                  </div>
                ) : user?.is_designer ? (
                  <div className="mb-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                      ✨ Verified Designer
                    </span>
                    {user?.designer_approved_at && (
                      <p className="text-xs text-green-600 mt-1">
                        Since: {(() => {
                          try {
                            const date = new Date(user.designer_approved_at);
                            return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
                          } catch (e) {
                            return 'N/A';
                          }
                        })()}
                      </p>
                    )}
                  </div>
                ) : designerApplication?.status === 'pending' ? (
                  <div className="mb-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                      ⏳ Pending Designer Status
                    </span>
                    <p className="text-xs text-yellow-600 mt-1">
                      Application under review
                    </p>
                  </div>
                ) : null}
                
                <div className="space-y-2 text-sm text-slate-500">
                  <p>Member since: {user.created_at ? (() => {
                    try {
                      const date = new Date(user.created_at);
                      return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
                    } catch (e) {
                      return 'N/A';
                    }
                  })() : 'N/A'}</p>
                  {user.email_verified && (
                    <p className="text-green-600">✓ Email verified</p>
                  )}
                  {user?.is_designer && user?.designer_approved_at && (
                    <p className="text-green-600">✓ Designer since: {user.designer_approved_at ? (() => {
                      try {
                        const date = new Date(user.designer_approved_at);
                        return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
                      } catch (e) {
                        return 'N/A';
                      }
                    })() : 'N/A'}</p>
                  )}
                </div>
                
                <button
                  onClick={logout}
                  className="mt-6 w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="space-y-6">
              
              {/* Quick Actions */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-900">Quick Actions</h3>
                  {user?.is_designer && (
                    <Badge className="bg-green-100 text-green-800">Designer Account</Badge>
                  )}
                </div>
                
                {/* Designer Welcome Message */}
                {user?.is_designer && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start space-x-3">
                      <div className="text-green-600 text-xl">🎉</div>
                      <div>
                        <h4 className="font-semibold text-green-900">Welcome to the Designer Program!</h4>
                        <p className="text-sm text-green-800 mt-1">
                          Your application has been approved. You can now upload and sell your 3D designs.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    onClick={navigateToQuote}
                    className="p-4 border border-slate-200 rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-colors text-left"
                  >
                    <div className="text-orange-600 text-2xl mb-2">📐</div>
                    <div className="font-medium text-slate-900">Get Quote</div>
                    <div className="text-sm text-slate-600">Upload 3D files for instant pricing</div>
                  </button>
                  
                  <button
                    onClick={navigateToServices}
                    className="p-4 border border-slate-200 rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-colors text-left"
                  >
                    <div className="text-orange-600 text-2xl mb-2">🔧</div>
                    <div className="font-medium text-slate-900">Services</div>
                    <div className="text-sm text-slate-600">Design and optimization services</div>
                  </button>
                  
                  <button
                    onClick={() => navigateToPage('marketplace')}
                    className="p-4 border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
                  >
                    <div className="text-blue-600 text-2xl mb-2">🛒</div>
                    <div className="font-medium text-slate-900">Designs</div>
                    <div className="text-sm text-slate-600">Browse and buy 3D designs</div>
                  </button>
                  
                  {user?.is_designer ? (
                    <button
                      onClick={() => navigateToPage('designer/dashboard')}
                      className="p-4 border border-green-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors text-left bg-green-50"
                    >
                      <div className="text-green-600 text-2xl mb-2">🎨</div>
                      <div className="font-medium text-slate-900">Designer Dashboard</div>
                      <div className="text-sm text-slate-600">Manage your designs & earnings</div>
                    </button>
                  ) : (
                    <button
                      onClick={() => navigateToPage('designer/apply')}
                      className="p-4 border border-purple-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors text-left"
                    >
                      <div className="text-purple-600 text-2xl mb-2">🎨</div>
                      <div className="font-medium text-slate-900">Become a Designer</div>
                      <div className="text-sm text-slate-600">Sell your 3D designs</div>
                    </button>
                  )}
                </div>
              </div>

              {/* Order History */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Recent Orders</h3>
                
                {loadingOrders ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-2"></div>
                    <p className="text-slate-600">Loading your orders...</p>
                  </div>
                ) : orders.length > 0 ? (
                  <div className="space-y-4">
                    {orders.map((order) => {
                      const orderType = getOrderType(order);
                      return orderType === 'product' ? (
                        <ProductOrderCard key={order.id} order={order} />
                      ) : (
                        <PrintingOrderCard key={order.id} order={order} />
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <div className="text-4xl mb-4">📦</div>
                    <p className="text-lg font-medium mb-2">No orders yet</p>
                    <p className="text-sm">Your order history will appear here once you place your first order.</p>
                    <button
                      onClick={navigateToQuote}
                      className="mt-4 bg-orange-600 text-white py-2 px-4 rounded-lg hover:bg-orange-700 transition-colors"
                    >
                      Start Your First Order
                    </button>
                  </div>
                )}
              </div>

              {/* Account Settings - Full Implementation */}
              <AccountSettings user={user} getIdToken={getIdToken} />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Order Details Modal */}
      {showOrderDetails && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Order Details</h2>
                  <p className="text-slate-600">Order #{selectedOrder.id.slice(0, 8)}...</p>
                </div>
                <button
                  onClick={() => setShowOrderDetails(false)}
                  className="text-slate-400 hover:text-slate-600 text-2xl font-bold"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6">
                {/* Order Type and Status */}
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      {getOrderType(selectedOrder) === 'product' ? selectedOrder.product_name : selectedOrder.filename}
                    </h3>
                    <p className="text-sm text-slate-600">
                      {getOrderType(selectedOrder) === 'product' ? 'Product Purchase' : '3D Printing Service'}
                    </p>
                  </div>
                  {getStatusBadge(selectedOrder.status)}
                </div>

                {/* Order Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {getOrderType(selectedOrder) === 'product' ? (
                    <>
                      <div>
                        <h4 className="font-medium text-slate-900 mb-2">Product Details</h4>
                        <div className="space-y-1 text-sm">
                          <p><span className="text-slate-500">Manufacturer:</span> {selectedOrder.product_manufacturer || 'N/A'}</p>
                          <p><span className="text-slate-500">Category:</span> {selectedOrder.product_category || 'N/A'}</p>
                          <p><span className="text-slate-500">Quantity:</span> {selectedOrder.quantity || 1}</p>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900 mb-2">Order Summary</h4>
                        <div className="space-y-1 text-sm">
                          <p><span className="text-slate-500">Price:</span> ${selectedOrder.final_price || selectedOrder.estimated_price}</p>
                          <p><span className="text-slate-500">Ordered:</span> {selectedOrder.created_at ? (() => {
                            try {
                              const date = new Date(selectedOrder.created_at);
                              return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
                            } catch (e) {
                              return 'N/A';
                            }
                          })() : 'N/A'}</p>
                          {selectedOrder.tracking_number && (
                            <p><span className="text-slate-500">Tracking:</span> {selectedOrder.tracking_number}</p>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <h4 className="font-medium text-slate-900 mb-2">Printing Specifications</h4>
                        <div className="space-y-1 text-sm">
                          <p><span className="text-slate-500">Material:</span> {selectedOrder.material}</p>
                          <p><span className="text-slate-500">Quality:</span> {selectedOrder.quality}</p>
                          <p><span className="text-slate-500">Color:</span> {selectedOrder.color}</p>
                          <p><span className="text-slate-500">Strength:</span> {selectedOrder.strength}</p>
                          <p><span className="text-slate-500">Smoothing:</span> {selectedOrder.smoothing}</p>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900 mb-2">Order Summary</h4>
                        <div className="space-y-1 text-sm">
                          <p><span className="text-slate-500">Price:</span> ${selectedOrder.estimated_price}</p>
                          <p><span className="text-slate-500">Delivery:</span> {selectedOrder.delivery_weeks} weeks</p>
                          <p><span className="text-slate-500">Created:</span> {selectedOrder.created_at ? (() => {
                            try {
                              const date = new Date(selectedOrder.created_at);
                              return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
                            } catch (e) {
                              return 'N/A';
                            }
                          })() : 'N/A'}</p>
                          {selectedOrder.tracking_number && (
                            <p><span className="text-slate-500">Tracking:</span> {selectedOrder.tracking_number}</p>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Customer Notes */}
                {selectedOrder.customer_notes && selectedOrder.customer_notes !== "Cart purchase" && (
                  <div>
                    <h4 className="font-medium text-slate-900 mb-2">Notes</h4>
                    <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                      {selectedOrder.customer_notes}
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => setShowOrderDetails(false)}
                    className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// ACCOUNT SETTINGS COMPONENT - Shipping, Billing, Email Notifications
// ============================================================================

const AccountSettings = ({ user, getIdToken }) => {
  const [activeTab, setActiveTab] = useState('shipping');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Shipping Addresses State
  const [addresses, setAddresses] = useState([]);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({
    name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    phone: '',
    is_default: false
  });

  // Payment Methods State
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showAddPaymentForm, setShowAddPaymentForm] = useState(false);

  // Email Preferences State
  const [emailPreferences, setEmailPreferences] = useState({
    order_confirmation: true,
    order_updates: true,
    shipping_notifications: true,
    delivery_notifications: true,
    marketing_emails: true,
    newsletter: true,
    promotions: true,
    designer_application_updates: true,
    design_sales_notifications: true,
    payout_notifications: true,
    account_security_alerts: true,
    password_changes: true
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const apiUrl = backendUrl.includes('None') || !backendUrl ? '' : backendUrl;

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'shipping') {
      loadAddresses();
    } else if (activeTab === 'billing') {
      loadPaymentMethods();
    } else if (activeTab === 'email') {
      loadEmailPreferences();
    }
  }, [activeTab]);

  // ========== SHIPPING ADDRESSES FUNCTIONS ==========
  
  const loadAddresses = async () => {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${apiUrl}/api/user/shipping-addresses`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAddresses(data.addresses);
      }
    } catch (error) {
      console.error('Error loading addresses:', error);
    }
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const idToken = await getIdToken();
      
      // Validate address first if US or Canada
      if (addressForm.country === 'US' || addressForm.country === 'CA') {
        const validateResponse = await fetch(`${apiUrl}/api/validate-address`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            address_line1: addressForm.address_line1,
            address_line2: addressForm.address_line2,
            city: addressForm.city,
            state_province: addressForm.state,
            postal_code: addressForm.postal_code,
            country: addressForm.country
          })
        });

        if (validateResponse.ok) {
          const validationResult = await validateResponse.json();
          if (!validationResult.valid) {
            setMessage({ 
              type: 'error', 
              text: 'Address validation failed: ' + validationResult.errors.join(', ')
            });
            setLoading(false);
            return;
          }
          
          // Use normalized address if provided
          if (validationResult.normalized_address) {
            addressForm.address_line1 = validationResult.normalized_address.address_line1;
            addressForm.city = validationResult.normalized_address.city;
            addressForm.state = validationResult.normalized_address.state_province;
            addressForm.postal_code = validationResult.normalized_address.postal_code;
          }
        }
      }
      
      const url = editingAddress
        ? `${apiUrl}/api/user/shipping-addresses/${editingAddress.id}`
        : `${apiUrl}/api/user/shipping-addresses`;
      
      const method = editingAddress ? 'PATCH' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(addressForm)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: `Address ${editingAddress ? 'updated' : 'added'} successfully!` });
        setShowAddressForm(false);
        setEditingAddress(null);
        resetAddressForm();
        await loadAddresses();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to save address' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving address' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;

    try {
      const idToken = await getIdToken();
      const response = await fetch(`${apiUrl}/api/user/shipping-addresses/${addressId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${idToken}` }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Address deleted successfully!' });
        await loadAddresses();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting address' });
    }
  };

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setAddressForm({
      name: address.name,
      address_line1: address.address_line1,
      address_line2: address.address_line2 || '',
      city: address.city,
      state: address.state,
      postal_code: address.postal_code,
      country: address.country,
      phone: address.phone || '',
      is_default: address.is_default
    });
    setShowAddressForm(true);
  };

  const resetAddressForm = () => {
    setAddressForm({
      name: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US',
      phone: '',
      is_default: false
    });
  };

  // ========== PAYMENT METHODS FUNCTIONS ==========
  
  const loadPaymentMethods = async () => {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${apiUrl}/api/user/payment-methods`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPaymentMethods(data.payment_methods);
      }
    } catch (error) {
      console.error('Error loading payment methods:', error);
    }
  };

  const handleSetDefaultPayment = async (methodId) => {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${apiUrl}/api/user/payment-methods/${methodId}/set-default`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${idToken}` }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Default payment method updated!' });
        await loadPaymentMethods();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error updating default payment method' });
    }
  };

  const handleDeletePayment = async (methodId) => {
    if (!window.confirm('Are you sure you want to delete this payment method?')) return;

    try {
      const idToken = await getIdToken();
      const response = await fetch(`${apiUrl}/api/user/payment-methods/${methodId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${idToken}` }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Payment method deleted successfully!' });
        await loadPaymentMethods();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting payment method' });
    }
  };

  // ========== EMAIL PREFERENCES FUNCTIONS ==========
  
  const loadEmailPreferences = async () => {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${apiUrl}/api/user/email-preferences`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmailPreferences(data.preferences);
      }
    } catch (error) {
      console.error('Error loading email preferences:', error);
    }
  };

  const handleEmailPreferenceChange = async (key, value) => {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${apiUrl}/api/user/email-preferences`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [key]: value })
      });

      if (response.ok) {
        setEmailPreferences(prev => ({ ...prev, [key]: value }));
        setMessage({ type: 'success', text: 'Email preferences updated!' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error updating preferences' });
    }
  };

  const handleSendTestEmail = async () => {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${apiUrl}/api/user/test-email-notification`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${idToken}` }
      });

      const data = await response.json();
      setMessage({ type: data.success ? 'success' : 'error', text: data.message });
    } catch (error) {
      setMessage({ type: 'error', text: 'Error sending test email' });
    }
  };

  // ========== RENDER ==========

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200">
      {/* Header */}
      <div className="p-6 border-b border-slate-200">
        <h3 className="text-lg font-semibold text-slate-900">Account Settings</h3>
        <p className="text-sm text-slate-600 mt-1">Manage your addresses, payment methods, and notifications</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex space-x-8 px-6">
          <button
            onClick={() => setActiveTab('shipping')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'shipping'
                ? 'border-orange-600 text-orange-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            📦 Shipping Addresses
          </button>
          <button
            onClick={() => setActiveTab('billing')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'billing'
                ? 'border-orange-600 text-orange-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            💳 Billing Information
          </button>
          <button
            onClick={() => setActiveTab('email')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'email'
                ? 'border-orange-600 text-orange-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            📧 Email Notifications
          </button>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`mx-6 mt-4 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Tab Content */}
      <div className="p-6">
        {/* SHIPPING ADDRESSES TAB */}
        {activeTab === 'shipping' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="font-medium text-slate-900">Your Shipping Addresses</h4>
              <button
                onClick={() => {
                  resetAddressForm();
                  setEditingAddress(null);
                  setShowAddressForm(true);
                }}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                + Add Address
              </button>
            </div>

            {/* Address Form */}
            {showAddressForm && (
              <form onSubmit={handleAddressSubmit} className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-4">
                <h5 className="font-medium text-slate-900">{editingAddress ? 'Edit Address' : 'New Address'}</h5>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Recipient Name *</label>
                    <input
                      type="text"
                      required
                      value={addressForm.name}
                      onChange={e => setAddressForm({...addressForm, name: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={addressForm.phone}
                      onChange={e => setAddressForm({...addressForm, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 1 *</label>
                  <input
                    type="text"
                    required
                    value={addressForm.address_line1}
                    onChange={e => setAddressForm({...addressForm, address_line1: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 2</label>
                  <input
                    type="text"
                    value={addressForm.address_line2}
                    onChange={e => setAddressForm({...addressForm, address_line2: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">City *</label>
                    <input
                      type="text"
                      required
                      value={addressForm.city}
                      onChange={e => setAddressForm({...addressForm, city: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">State/Province *</label>
                    <input
                      type="text"
                      required
                      value={addressForm.state}
                      onChange={e => setAddressForm({...addressForm, state: e.target.value})}
                      placeholder="e.g., CA, ON"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">ZIP/Postal Code *</label>
                    <input
                      type="text"
                      required
                      value={addressForm.postal_code}
                      onChange={e => setAddressForm({...addressForm, postal_code: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Country *</label>
                  <select
                    required
                    value={addressForm.country}
                    onChange={e => setAddressForm({...addressForm, country: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="US">United States</option>
                    <option value="Canada">Canada</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_default"
                    checked={addressForm.is_default}
                    onChange={e => setAddressForm({...addressForm, is_default: e.target.checked})}
                    className="w-4 h-4 text-orange-600 border-slate-300 rounded focus:ring-orange-500"
                  />
                  <label htmlFor="is_default" className="ml-2 text-sm text-slate-700">
                    Set as default shipping address
                  </label>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:bg-slate-300"
                  >
                    {loading ? 'Saving...' : (editingAddress ? 'Update Address' : 'Add Address')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddressForm(false);
                      setEditingAddress(null);
                      resetAddressForm();
                    }}
                    className="px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {/* Address List */}
            {addresses.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <div className="text-4xl mb-4">📦</div>
                <p>No shipping addresses saved</p>
                <p className="text-sm mt-2">Add an address to speed up your checkout process</p>
              </div>
            ) : (
              <div className="space-y-3">
                {addresses.map(address => (
                  <div key={address.id} className="border border-slate-200 rounded-lg p-4 hover:border-orange-300 transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="font-medium text-slate-900">{address.name}</span>
                          {address.is_default && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Default</span>
                          )}
                        </div>
                        <div className="text-sm text-slate-600 space-y-1">
                          <p>{address.address_line1}</p>
                          {address.address_line2 && <p>{address.address_line2}</p>}
                          <p>{address.city}, {address.state} {address.postal_code}</p>
                          <p>{address.country}</p>
                          {address.phone && <p>Phone: {address.phone}</p>}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditAddress(address)}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteAddress(address.id)}
                          className="text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* BILLING INFORMATION TAB */}
        {activeTab === 'billing' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="font-medium text-slate-900">Your Payment Methods</h4>
              <button
                onClick={() => setShowAddPaymentForm(true)}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                + Add Payment Method
              </button>
            </div>

            {/* Add Payment Method Notice */}
            {showAddPaymentForm && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h5 className="font-medium text-blue-900 mb-2">Add Payment Method</h5>
                <p className="text-sm text-blue-800 mb-3">
                  Payment method integration will be completed once Stripe API keys are provided.
                  This feature uses Stripe's secure payment processing to safely store your card information.
                </p>
                <button
                  onClick={() => setShowAddPaymentForm(false)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Close
                </button>
              </div>
            )}

            {/* Payment Methods List */}
            {paymentMethods.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <div className="text-4xl mb-4">💳</div>
                <p>No payment methods saved</p>
                <p className="text-sm mt-2">Add a payment method for faster checkout</p>
                <p className="text-xs mt-3 text-slate-400">Secure payment processing powered by Stripe</p>
              </div>
            ) : (
              <div className="space-y-3">
                {paymentMethods.map(method => (
                  <div key={method.id} className="border border-slate-200 rounded-lg p-4 hover:border-orange-300 transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="font-medium text-slate-900">
                            {method.card_brand.toUpperCase()} •••• {method.card_last4}
                          </span>
                          {method.is_default && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Default</span>
                          )}
                        </div>
                        <div className="text-sm text-slate-600 space-y-1">
                          <p>Expires: {method.card_exp_month}/{method.card_exp_year}</p>
                          <p>{method.billing_name}</p>
                          <p>{method.billing_address_line1}, {method.billing_city}, {method.billing_state} {method.billing_postal_code}</p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        {!method.is_default && (
                          <button
                            onClick={() => handleSetDefaultPayment(method.id)}
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                          >
                            Set Default
                          </button>
                        )}
                        <button
                          onClick={() => handleDeletePayment(method.id)}
                          className="text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* EMAIL NOTIFICATIONS TAB */}
        {activeTab === 'email' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h4 className="font-medium text-slate-900">Email Notification Preferences</h4>
              <button
                onClick={handleSendTestEmail}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              >
                Send Test Email
              </button>
            </div>

            {/* Order Notifications */}
            <div>
              <h5 className="font-medium text-slate-900 mb-3">Order Notifications</h5>
              <div className="space-y-3">
                <EmailToggle
                  label="Order Confirmation"
                  description="Receive confirmation emails when you place an order"
                  checked={emailPreferences.order_confirmation}
                  onChange={(val) => handleEmailPreferenceChange('order_confirmation', val)}
                />
                <EmailToggle
                  label="Order Updates"
                  description="Get notified about order status changes"
                  checked={emailPreferences.order_updates}
                  onChange={(val) => handleEmailPreferenceChange('order_updates', val)}
                />
                <EmailToggle
                  label="Shipping Notifications"
                  description="Receive tracking info when your order ships"
                  checked={emailPreferences.shipping_notifications}
                  onChange={(val) => handleEmailPreferenceChange('shipping_notifications', val)}
                />
                <EmailToggle
                  label="Delivery Notifications"
                  description="Get notified when your order is delivered"
                  checked={emailPreferences.delivery_notifications}
                  onChange={(val) => handleEmailPreferenceChange('delivery_notifications', val)}
                />
              </div>
            </div>

            {/* Marketing Notifications */}
            <div>
              <h5 className="font-medium text-slate-900 mb-3">Marketing & Promotions</h5>
              <div className="space-y-3">
                <EmailToggle
                  label="Marketing Emails"
                  description="Receive promotional offers and product updates"
                  checked={emailPreferences.marketing_emails}
                  onChange={(val) => handleEmailPreferenceChange('marketing_emails', val)}
                />
                <EmailToggle
                  label="Newsletter"
                  description="Get our monthly newsletter with tips and news"
                  checked={emailPreferences.newsletter}
                  onChange={(val) => handleEmailPreferenceChange('newsletter', val)}
                />
                <EmailToggle
                  label="Special Promotions"
                  description="Be the first to know about sales and discounts"
                  checked={emailPreferences.promotions}
                  onChange={(val) => handleEmailPreferenceChange('promotions', val)}
                />
              </div>
            </div>

            {/* Designer Notifications (if user is a designer) */}
            {user.is_designer && (
              <div>
                <h5 className="font-medium text-slate-900 mb-3">Designer Notifications</h5>
                <div className="space-y-3">
                  <EmailToggle
                    label="Application Updates"
                    description="Status updates on your designer application"
                    checked={emailPreferences.designer_application_updates}
                    onChange={(val) => handleEmailPreferenceChange('designer_application_updates', val)}
                  />
                  <EmailToggle
                    label="Sales Notifications"
                    description="Get notified when someone purchases your designs"
                    checked={emailPreferences.design_sales_notifications}
                    onChange={(val) => handleEmailPreferenceChange('design_sales_notifications', val)}
                  />
                  <EmailToggle
                    label="Payout Notifications"
                    description="Receive updates about your earnings and payouts"
                    checked={emailPreferences.payout_notifications}
                    onChange={(val) => handleEmailPreferenceChange('payout_notifications', val)}
                  />
                </div>
              </div>
            )}

            {/* Account Security */}
            <div>
              <h5 className="font-medium text-slate-900 mb-3">Account Security</h5>
              <div className="space-y-3">
                <EmailToggle
                  label="Security Alerts"
                  description="Important security notifications (recommended)"
                  checked={emailPreferences.account_security_alerts}
                  onChange={(val) => handleEmailPreferenceChange('account_security_alerts', val)}
                />
                <EmailToggle
                  label="Password Changes"
                  description="Get notified when your password is changed"
                  checked={emailPreferences.password_changes}
                  onChange={(val) => handleEmailPreferenceChange('password_changes', val)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Email Toggle Component
const EmailToggle = ({ label, description, checked, onChange }) => {
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100">
      <div>
        <div className="font-medium text-slate-900">{label}</div>
        <div className="text-sm text-slate-600">{description}</div>
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-orange-600' : 'bg-slate-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
};

export default Profile;